import React, { createRef, useContext, useMemo, useState } from "react";
import { cloneDeep } from "lodash";
import { RouteComponentProps } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import {
  FEATURE_FLAG_NAME,
  SUBMISSION_TYPE_MODULE,
  SUPPORT_EMAIL,
} from "common/constants";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { getObjectDisplayForAttached } from "common-client/utils/customObject";
import {
  useGetPropertyForSubmissionQuery,
  useGetSisdRuleDataQuery,
  useGetSubmissionQuery,
  useRegenerateAiFieldsMutation,
  useUpdateSubmissionMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { setTitle } from "../../utils/title";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Button } from "../Common/Button";
import { EmptyState } from "../Common/EmptyState";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import FullPageLoadingScreen from "../Common/FullPageLoadingScreen";
import AccessDeniedPage, {
  ACCESS_DENIED_RESOURCE,
} from "../Guest/AccessDeniedPage";
import { LinkedTask } from "../Tasks/LinkedTask";
import { DescriptionContainer } from "./__styles__/CreateSubmission";
import { ButtonContainer, EmptyStateContainer } from "./__styles__/Submission";
import ActionButton from "./ActionButton";
import useSubmissionsChatAgent, {
  createFormUpdateHandler,
} from "./ChatAgent/SubmissionsChatAgent";
import { Content } from "./Content";
import { FormDataType } from "./Form";
import SubmissionHeader from "./SubmissionHeader";
import {
  generateUpsertErrorMessage,
  hasSummary,
  useSubmissionPrevLocation,
} from "./utils";

interface URLParams {
  submissionId: string;
}

export interface EditSubmissionProps extends RouteComponentProps<URLParams> {}

const EditSubmission = ({ match }: EditSubmissionProps) => {
  const { authorized, isPublic, account } = useContext(AuthContext);
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const [disabled, setDisabled] = useState(true);
  const [serverProvidedFormData, setServerProvidedFormData] =
    useState<FormDataType | null>(null);
  const [regeneratingPaths, setRegeneratingPaths] = useState<
    Record<string, boolean>
  >({});

  const { submissionId } = match.params;

  const { data, loading, error, refetch } = useGetSubmissionQuery({
    variables: { submissionId, isPublic },
    fetchPolicy: "cache-and-network",
  });

  const [updateSubmission, { loading: updateLoading }] =
    useUpdateSubmissionMutation({
      onCompleted: data => {
        const updatedSubmission = data.updateSubmission;
        track("Submission updated", {
          submissionId: updatedSubmission.id,
          "Submission Category": submissionType.category,
          "Submission Type": submissionType.name,
        });
        addSuccessToast("Your submission has been updated.");

        return refetch();
      },
      onError: error => {
        const message = generateUpsertErrorMessage(error);
        addErrorToast(message);
      },
    });

  const submission = data?.submission;
  const propertyId = submission?.property?.id;
  const { data: propertyData, loading: propertyDataLoading } =
    useGetPropertyForSubmissionQuery({
      variables: { propertyId: propertyId! },
      skip: !propertyId,
    });

  const submissionTypeModules =
    submission?.submissionTypeVersion.submissionType.modules;

  const { data: sisdRuleData, loading: sisdRuleDataLoading } =
    useGetSisdRuleDataQuery({
      variables: { propertyId: propertyId! },
      skip:
        !submissionTypeModules?.includes(
          SUBMISSION_TYPE_MODULE.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE,
        ) || !propertyId,
    });

  const prevLocation = useSubmissionPrevLocation();

  const handleFormUpdate = useMemo(
    () =>
      createFormUpdateHandler({
        setServerProvidedFormData,
        setDisabled,
        addSuccessToast,
      }),
    [setDisabled, addSuccessToast],
  );

  const chatAgentConfig = useMemo(
    () => ({
      isPublic,
      submissionTypeVersionId: submission?.submissionTypeVersion?.id,
      formData: submission?.formData ?? {},
      onFormUpdate: handleFormUpdate,
    }),
    [
      isPublic,
      submission?.submissionTypeVersion?.id,
      submission?.formData,
      handleFormUpdate,
    ],
  );

  const {
    ChatToggleButton,
    ChatPanel: SidePanel,
    isChatPanelOpen: isSidePanelOpen,
  } = useSubmissionsChatAgent(chatAgentConfig);

  const [regenerateAIFields, { loading: regenerateAIFieldsLoading }] =
    useRegenerateAiFieldsMutation();

  const propertyProp = useMemo(() => {
    return {
      FIRMInfo: sisdRuleData?.FIRMInfo,
    };
  }, [sisdRuleData?.FIRMInfo]);

  if (
    (loading && !data) ||
    (propertyDataLoading && !propertyData) ||
    (sisdRuleDataLoading && !sisdRuleData)
  ) {
    return <FullPageLoadingScreen />;
  }

  if (error || !submission) {
    if (isPublic) {
      return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.RECORD} />;
    }

    return (
      <FullPageFormLayout
        subtitle={""}
        prevLocation={prevLocation}
        rightContainer={<div />}
      >
        <EmptyStateContainer>
          <EmptyState
            title="This record is unavailable"
            body={
              <>
                You may not have access to this file, or it may have been
                removed.
              </>
            }
            icon="missing-file"
            buttonLinkProps={{
              label: "Get help",
              onClick: () =>
                window.open(`https://forerunner.helpscoutdocs.com/`),
            }}
            primaryButtonProps={{
              label: "Contact us",
              onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`),
            }}
          />
        </EmptyStateContainer>
      </FullPageFormLayout>
    );
  }

  const submitFormRef = createRef<HTMLButtonElement>();

  const submissionType = submission.submissionTypeVersion.submissionType;

  if (isPublic) {
    setTitle(submissionType.name);
  }

  const canUpdateSubmission = authorized({
    resource: RESOURCE_NAME.SUBMISSION,
    permission: "update",
  });

  const handleSubmit = async (formData: FormDataType) => {
    await updateSubmission({
      variables: { data: { submissionId, formData } },
    });
  };

  const handleRegenerateAIFields = async (
    formData: FormDataType,
    paths: string[],
  ) => {
    setRegeneratingPaths(previousPaths => {
      const clonedPreviousPaths = cloneDeep(previousPaths);
      paths.forEach(path => {
        clonedPreviousPaths[path] = true;
      });
      return clonedPreviousPaths;
    });

    await regenerateAIFields({
      variables: { data: { submissionId, formData, paths } },
      onCompleted: data => {
        setServerProvidedFormData(data.regenerateAIFields);

        setRegeneratingPaths(previousPaths => {
          const clonedPreviousPaths = cloneDeep(previousPaths);
          paths.forEach(path => {
            clonedPreviousPaths[path] = false;
          });
          return clonedPreviousPaths;
        });
        setDisabled(false);
        addSuccessToast("AI fields regenerated");
      },
    });
  };

  const rightContainer = (
    <ButtonContainer>
      <ActionButton
        isEditView={true}
        submission={{
          ...submission,
          hasSummary: hasSummary(submission),
          category: submissionType.category,
        }}
        objectIsHiddenFromPublic={submission.property?.hiddenFromPublic}
      />
      {!isPublic && (
        <>
          <Button
            size="small"
            styleVariant="primary"
            onClick={() => {
              submitFormRef.current!.click();
            }}
            disabled={!canUpdateSubmission || disabled}
            loading={updateLoading || regenerateAIFieldsLoading}
          >
            Update
          </Button>
          {ChatToggleButton}
        </>
      )}
    </ButtonContainer>
  );

  const tasks = submission.tasks;
  const canViewTasks = authorized({
    permission: "view",
    resource: RESOURCE_NAME.TASK,
  });
  const tasksFeature = account?.featureFlags.find(
    ff => ff.feature === FEATURE_FLAG_NAME.TASKS_WEB,
  );

  return (
    <FullPageFormLayout
      subtitle={submissionType.name}
      prevLocation={prevLocation}
      rightContainer={rightContainer}
      isSidePanelOpen={isSidePanelOpen}
      sidePanel={SidePanel}
      width="regular"
    >
      <div>
        <SubmissionHeader
          account={{
            logoUrl: account?.logoUrl ?? "",
            name: account?.name ?? "",
          }}
          objectDisplay={getObjectDisplayForAttached(submission)}
          submissionType={submissionType}
          hiddenFromPublic={submission.hiddenFromPublic}
        >
          {submissionType.description && (
            <DescriptionContainer markdown={submissionType.description} />
          )}
        </SubmissionHeader>
        {tasksFeature?.isEnabled &&
          canViewTasks &&
          arrayHasAtLeastOneItem(tasks) && <LinkedTask tasks={tasks} />}
        <Content
          submission={submission}
          handleRegenerateAIFields={handleRegenerateAIFields}
          regeneratingPaths={regeneratingPaths}
          setFormDisabled={setDisabled}
          propertyProp={propertyProp}
          property={propertyData?.property}
          serverProvidedFormData={serverProvidedFormData}
          onSubmit={handleSubmit}
          account={sisdRuleData?.account}
          submitFormRef={submitFormRef}
        />
      </div>
    </FullPageFormLayout>
  );
};

export default EditSubmission;
